import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ServiceAbonnementDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/service-abonnement-dto';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { ServiceaftaleTagDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-tag-dto';
import { OpkraevningsDialogData, OpkraevningsDialogResponse } from '../../../models';
import { OpkraevningsIntervalDialogComponent } from './opkraevnings-interval-dialog/opkraevnings-interval-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { PartnerBaseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/partner-base-dto';
import { Observable, Subject } from 'rxjs';
import { LogicService } from 'src/app/services/logic.service';
import { HenviserDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/henviser-dto';
import { ProduktEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/produkt-enum';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth.service';
import { ChangeEffect } from 'src/app/api/models/OK/Egis/Service/Models/Request/change-effect';
import { PostnummerServicePartnerEntity } from 'src/app/api/models/OK/Egis/Service/Models/Entities/postnummer-service-partner-entity';

@Component({
  selector: 'app-abonnement-form',
  templateUrl: './abonnement-form.component.html',
  styleUrls: ['./abonnement-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AbonnementFormComponent implements OnInit, OnDestroy {
  @Input() form: UntypedFormGroup;
  @Input() tags: ServiceaftaleTagDto[];
  @Input() subscribtions: ServiceAbonnementDto[];
  @Input() partners: PostnummerServicePartnerEntity[];
  @Input() henvisere: HenviserDto[];
  @Input() produktId: ProduktEnum;
  @Input() isanlaegNewlyInstalled: boolean;

  @Output() abonnementChange: EventEmitter<number> = new EventEmitter();
  @Output() selectedPartner = new EventEmitter<PostnummerServicePartnerEntity>();
  @Output() startdateChanged = new EventEmitter<any>();

  selectedSubscription: ServiceAbonnementDto;
  currentPartner: PostnummerServicePartnerEntity;

  filteredPartners: Observable<PartnerBaseDto[]>;

  checkupInterval: Number;
  today: Date = new Date();
  minPaymentDate: Date = new Date();
  maxPaymentDate = new Date();
  tagIdList: number[] = [];

  canWrite: boolean = false;

  unsubscribe$ = new Subject<void>();

  get partnerNo() {
    return this.form.get('partnerNo');
  }

  get henviserCvr() {
    return this.form.get('henviserCvr');
  }

  get abonnementId() {
    return this.form.get('abonnementId');
  }

  get isActive() {
    return this.form.get('isActive');
  }

  get startDate() {
    return this.form.get('startDate');
  }

  get nextCheckupDate() {
    return this.form.get('nextCheckupDate');
  }

  get serviceOrderNote() {
    return this.form.get('serviceOrderNote');
  }

  get fastOrdreNote() {
    return this.form.get('fastOrdreNote');
  }

  get opkraevningDate() {
    return this.form.get('opkraevningDate');
  }

  get tagIds() {
    return this.form.get('tagIds');
  }

  get opkraevningsinterval() {
    return this.form.get('opkraevningsinterval');
  }

  get eftersynInterval() {
    return this.form.get('eftersynInterval');
  }

  constructor(
    private dialog: MatDialog,
    private logic: LogicService,
    private auth: AuthService,
  ) {
    this.canWrite = this.auth.canWrite;
    console.log('canWrite', this.auth.canWrite);
  }

  ngOnInit(): void {
    this.startDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      let seedDate = new Date(this.startDate.value);
      this.minPaymentDate = new Date(seedDate > this.today ? seedDate : this.today);
      //Use start date, or if in past, use todat
      if (seedDate < this.today) {
        seedDate = new Date(this.today);
      }
      this.nextCheckupDate.setValue(this.logic.calculateDefaultCheckupDate(seedDate));
    });

    //Initialize with 3 years
    this.maxPaymentDate = new Date(this.maxPaymentDate.setMonth(this.maxPaymentDate.getMonth() + 36));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  getMinCheckupDate(): Date {
    //checkup date should not be in the past, and not smaller than startdate
    return this.today >= this.startDate.value ? this.today : this.startDate.value;
    //new Date(Math.max.apply(null, [this.today, this.startDate.value]));
  }

  private getMaxPaymentDate(): Date {
    let opkravningsInterval: number = this.opkraevningsinterval?.value
      ? this.opkraevningsinterval.value
      : this.selectedSubscription?.opkraevningInterval;

    //default to 3 years
    opkravningsInterval = opkravningsInterval ? opkravningsInterval : 36;

    let startDate = this.startDate.value ? new Date(this.startDate.value) : new Date();
    return new Date(startDate.setMonth(startDate.getMonth() + opkravningsInterval));
  }

  onSelectedPartnerChange(partner: PostnummerServicePartnerEntity) {
    this.currentPartner = partner;
    this.selectedPartner.emit(partner);
  }

  onSubscribtionChange(id: number): void {
    this.selectedSubscription = this.subscribtions.find((x) => x.id == id);
    this.maxPaymentDate = this.getMaxPaymentDate();
    this.abonnementChange.emit(id);
    if (!this.currentPartner) {
      this.currentPartner = this.partners[0];
      this.selectedPartner.emit(this.partners[0]);
    }
    this.eftersynInterval.setValue(this.selectedSubscription.eftersynInterval);
  }

  onStartDateChanged(startDateEvent: MatDatepickerInputEvent<any>) {
    //Update checkup date to be 30 days after startdate or today
    let date = this.logic.calculateDefaultCheckupDate(startDateEvent.value);
    this.maxPaymentDate = this.getMaxPaymentDate();
    this.nextCheckupDate.setValue(date > this.today ? date : this.today);

    this.startdateChanged.emit(startDateEvent.value);
    this.startDate.setValue(startDateEvent.value);
  }

  onTagToggle(tagId: number) {
    if (this.tagIdList.indexOf(tagId) > -1) {
      this.tagIdList.splice(this.tagIdList.indexOf(tagId), 1);
    } else {
      this.tagIdList.push(tagId);
    }
    this.tagIds.setValue(this.tagIdList);
  }

  openOpkraevningsIntervalDialog() {
    let data: OpkraevningsDialogData = {
      changeEffect: ChangeEffect.Instant,
      opkraevningsInterval: this.opkraevningsinterval.value
        ? this.opkraevningsinterval.value
        : this.selectedSubscription.opkraevningInterval,
      opkraevningDate: new Date(this.opkraevningDate.value),
    };

    const dialogRef = this.dialog.open(OpkraevningsIntervalDialogComponent, {
      data: data,
    });

    dialogRef.afterClosed().subscribe((result: OpkraevningsDialogResponse) => {
      if (result) {
        this.opkraevningsinterval.setValue(result.opkraevningsInterval);
        this.maxPaymentDate = this.getMaxPaymentDate();
      }
    });
  }

  clearOpkraevningsInterval() {
    this.opkraevningsinterval.setValue(null);
  }
}
